import React, { useEffect, useState } from "react";
import { Tab } from "@headlessui/react";
import AnchorChip from "@/components/lib/AnchorChip";
import { getEvents } from "@/actions/eventsActions";
import { useEntries } from "@/actions/entriesActions";
import { useProjections } from "@/actions/eventsActions";
import { useMovies } from "@/actions/useMovies";
import Projections from "@/components/film/Projections";
import EventCard from "@/components/event/EventCard";
import useWindowSize from "@/hooks/useWindowSize";
import breakpoints from "@/constants/breakpoinst";
import BlogPoster from "@/components/blog/BlogPoster";
import PosterCard from "@/components/film/PosterCard";
import { getUnixTime } from "date-fns";
import * as Text from "@/components/text";
import { localeTexts } from "@/utils/locale";
import { useRouter } from "next/router";
import { getStrapiImage } from "@/utils/strapiFunctions";

export default function RelatedContent({
  movieId,
  showMovie = false,
  showEvents = true,
  showEntries = true,
  showProjections = true,
}) {
  const { width } = useWindowSize();
  const [events, setEvents] = useState({});
  let now = getUnixTime(new Date()) * 1000;

  const router = useRouter();
  const [locale, setLocale] = useState("es-MX");
  
  useEffect(() => {
  if (!router.isReady) return;
  const urlLocale = router.query.locale;
  const storedLocale = typeof window !== "undefined"
  ? localStorage.getItem("amb-locale")
  : null;
  
  const finalLocale = urlLocale || storedLocale || "es-MX";
  setLocale(finalLocale);
  localStorage.setItem("amb-locale", finalLocale);
  }, [router.isReady, router.query.locale]);

  useEffect(() => {
    getEvents({
      peliculas: {
        id: movieId,
      },
    }).then((response) => {
      setEvents(response.data);
    });
  }, []);

  const {
    data: entries,
    isLoading: entriesLoading,
    isError: entriesError,
  } = useEntries({
    peliculas: {
      id: movieId,
    },
  });

  const {
    data: projections,
    isLoading: projectionsLoading,
    isError: projectionsError,
  } = useProjections({
    peliculas: {
      id: movieId,
    },
    $or: [
      {
        tv_screenings: {
          date: {
            $gte: now,
          },
        },
      },

      {
        live_screenings: {
          date: {
            $gte: now,
          },
        },
      },

      {
        online_screenings: {
          start_date: {
            $gte: now,
          },
        },
      },
    ],
  });

  const {
    data: movies,
    isLoading: moviesLoading,
    isError: moviesError,
  } = useMovies({
    id: movieId,
  });

  const dataSources = [
    projections?.data?.length,
    entries?.data?.length,
    events?.data?.length,
  ].filter(Boolean);

  return (
    <div className="">
      {(movies?.data?.length && showMovie) ||
      (projections?.data?.length && showProjections) ||
      (events?.data?.length && showEvents) ||
      (entries?.data?.length && showEntries) ? (
        <div className="my-8 text-center">
          <Text.Display
            size={width < breakpoints.md ? "xs" : "md"}
            fontWeight="light"
          >
            {localeTexts[locale].RelatedContent.title}
          </Text.Display>
        </div>
      ) : null}
      <Tab.Group>
        {dataSources.length >= 2 && (
          <Tab.List className="space-x-3 text-center">
            {movies?.data?.length && showMovie ? (
              <Tab>
                {({ selected }) => (
                  <AnchorChip active={selected}> {localeTexts[locale].RelatedContent.AnchorChip.films}</AnchorChip>
                )}
              </Tab>
            ) : null}

            {projections?.data?.length && showProjections ? (
              <Tab>
                {({ selected }) => (
                  <AnchorChip active={selected}> {localeTexts[locale].RelatedContent.AnchorChip.screenings}</AnchorChip>
                )}
              </Tab>
            ) : null}

            {events?.data?.length && showEvents ? (
              <Tab>
                {({ selected }) => (
                  <AnchorChip active={selected}> {localeTexts[locale].RelatedContent.AnchorChip.events}</AnchorChip>
                )}
              </Tab>
            ) : null}
            {entries?.data?.length && showEntries ? (
              <Tab>
                {({ selected }) => (
                  <AnchorChip active={selected}>{localeTexts[locale].RelatedContent.AnchorChip.publications}</AnchorChip>
                )}
              </Tab>
            ) : null}
          </Tab.List>
        )}
        <Tab.Panels>
          {movies?.data?.length && showMovie ? (
            <Tab.Panel className="mt-10">
              <div className="grid-cols-4 gap-3 space-y-10 md:grid md:space-y-0">
                {movies?.data?.length > 0
                  ? movies.data.map((movie) => (
                      <PosterCard
                        key={movie.id}
                        grid_details={true}
                        theme="light"
                        slug={`/catalogo/${movie.attributes.filmSlug}`}
                        film_name={movie.attributes.film_name}
                        film_image={
                          movie.attributes?.film_cover_image.data?.attributes
                            .formats.small?.url ||
                          movie.attributes?.film_cover_image.data?.attributes
                            .url
                        }
                        film_link={movie.attributes.film_stream_link || ""}
                        film_directors={movie.attributes.Directores.map(
                          ({ Director }) => Director
                        )}
                        film_synopsis={movie.attributes.film_excerpt}
                        film_streaming_link={
                          movie.attributes.film_stream_link || ""
                        }
                      />
                    ))
                  : null}
              </div>
            </Tab.Panel>
          ) : null}

          {projections?.data?.length ? (
            <Tab.Panel className="mt-10">
              <div className="grid-cols-2 gap-3 md:grid">
                {projections?.data?.length > 0
                  ? projections.data.map((projection) => {
                      if (projection.attributes?.online_screenings[0]) {
                        return (
                          <Projections
                            key={projection.id}
                            label={"Proyección"}
                            fullDate={true}
                            movie_title={
                              projection.attributes?.online_screenings[0]?.title
                            }
                            showTitle={true}
                            showImage={false}
                            maps_url={
                              projection.attributes?.sede?.data?.attributes
                                ?.venue_map_url
                            }
                            trailer={
                              projection.attributes?.peliculas?.data[0]
                                ?.attributes?.film_trailer_link
                            }
                            movie_link={
                              projection.attributes?.online_screenings
                                ? `/catalogo/${projection.attributes.films?.data[0]?.attributes?.slug}`
                                : `/calendario/${projection.id}`
                            }
                            view_more={`/catalogo/${projection.attributes?.peliculas?.data[0]?.attributes.filmSlug}`}
                            movie={projection.attributes.peliculas.data[0]}
                            image={
                              projection.attributes?.event_image?.data
                                ?.attributes?.url ||
                              projection.attributes?.peliculas?.data[0]
                                ?.attributes?.film_cover_image?.data?.attributes
                                ?.url
                            }
                            film_projections={{
                              from_date:
                                projection.attributes?.online_screenings[0]
                                  ?.start_date,
                              to_date:
                                projection.attributes?.online_screenings[0]
                                  ?.end_date,
                              cost: projection?.attributes?.online_screenings[0]
                                ?.admission_fee,
                              venue:
                                projection?.attributes?.sede?.data?.attributes
                                  ?.name || "",
                              city:
                                projection?.attributes?.sede?.data?.attributes
                                  ?.states || "",
                            }}
                          />
                        );
                      } else {
                        return (
                          <Projections
                            key={projection.id}
                            fullDate={true}
                            label={"Proyección"}
                            movie_title={
                              projection.attributes?.event_name ||
                              projection.attributes?.title
                            }
                            showTitle={true}
                            showImage={false}
                            view_more={
                              projection.attributes?.live_screenings
                                ? `/catalogo/${projection.attributes?.peliculas?.data[0]?.attributes.filmSlug}`
                                : `/calendario/${projection.id}`
                            }
                            movie_link={
                              projection.attributes?.live_screenings
                                ? ""
                                : `/calendario/${projection.id}`
                            }
                            movie={projection.attributes.peliculas.data[0]}
                            image={
                              projection.attributes?.event_image?.data
                                ?.attributes?.url ||
                              projection.attributes?.peliculas?.data[0]
                                ?.attributes?.film_cover_image?.data?.attributes
                                ?.url
                            }
                            film_projections={{
                              maps_url:
                                projection.attributes?.sede?.data?.attributes
                                  ?.venue_map_url,
                              from_date:
                                projection.attributes?.live_screenings[0]?.date,
                              to_date:
                                projection.attributes?.live_screenings[0]?.date,
                              cost: projection?.attributes?.live_screenings[0]
                                ?.admission_fee,
                              venue:
                                projection?.attributes?.sede?.data?.attributes
                                  ?.name || "",
                              city:
                                projection?.attributes?.sede?.data?.attributes
                                  ?.states || "",
                            }}
                          />
                        );
                      }
                    })
                  : null}
              </div>
            </Tab.Panel>
          ) : null}
          {events?.data?.length ? (
            <Tab.Panel className="mt-10">
              <div className="grid-cols-4 gap-3 space-y-10 md:grid md:space-y-0">
                {events?.data?.length > 0
                  ? events?.data.map((event) => (
                      <EventCard
                        key={event.id}
                        size={width < breakpoints.md ? "lg" : "md"}
                        event_type={event.attributes.event_type}
                        event_title={event.attributes.event_name}
                        date_time={event.attributes.date_time}
                        event_state={
                          event?.attributes?.sede?.data?.attributes?.states
                        }
                        event_venue={
                          event.attributes?.sede?.data?.attributes?.name || ""
                        }
                        event_image_url={
                          getStrapiImage(event?.attributes?.event_image, "small")
                        }
                        event_url={`/calendario/${event.id}`}
                      />
                    ))
                  : null}
              </div>
            </Tab.Panel>
          ) : null}
          {entries?.data?.length ? (
            <Tab.Panel className="mt-10">
              <div className="grid-cols-4 gap-3 md:grid">
                {entries?.data?.length > 0
                  ? entries.data.map((post) => (
                      <BlogPoster
                        key={post.id}
                        grid_details={true}
                        theme="light"
                        slug={`/blog/${post.attributes.slug}`}
                        title={post.attributes.titulo}
                        date={new Date(post.attributes.publishedAt)}
                        description={post.attributes.contenido}
                        tag={
                          post.attributes.categorias?.data[0]?.attributes
                            ?.name || (post.type === "post" ? localeTexts[locale].RelatedContent.Blogposter.news : localeTexts[locale].RelatedContent.Blogposter.note)
                        }
                        image={
                          post.attributes.cover_image.data?.attributes?.formats
                            ?.medium?.url
                        }
                        metadata={{
                          date: new Date(post.attributes.publishedAt),
                          type: post.type === "post" ? "Noticia" : "",
                          length: "",
                        }}
                      />
                    ))
                  : null}
              </div>
            </Tab.Panel>
          ) : null}
        </Tab.Panels>
      </Tab.Group>
    </div>
  );
}
